import { toastFailure } from '@/lib/feedback'
import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toastDeferredDelete } from '@/lib/toastUndo'
import {
  clearAllNotifications,
  deleteNotification,
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
} from './api'
import type { Notification, NotificationType } from './types'

/**
 * Notifications data hook for the sidebar bell/inbox.
 *
 * Freshness strategy: an initial fetch (list + unread count) seeds the state; a
 * Supabase realtime subscription on `core.notifications` filtered to the current
 * user refetches on INSERT/UPDATE; and `refetch` is called on open as a safe
 * fallback for when realtime is unavailable (the table must be in the
 * `supabase_realtime` publication for push to fire — until then the fetch +
 * refetch-on-open path keeps the UI correct).
 *
 * `typeFilter` (spec-35 phase 5) narrows the *list* to a chip's types via the
 * repeated `?types=` param; `null` means Alles. The unread badge stays global
 * (never filtered), so switching chips never changes the count.
 */
export function useNotifications(recipientId: string | null) {
  const [items, setItems] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [typeFilter, setTypeFilter] = useState<NotificationType[] | null>(null)

  const refetch = useCallback(async () => {
    try {
      const [rows, count] = await Promise.all([
        listNotifications({ limit: 50, types: typeFilter ?? undefined }),
        getUnreadCount(),
      ])
      setItems(rows)
      setUnreadCount(count)
    } catch {
      // Leave the last-known state in place; the next open retries.
    }
  }, [typeFilter])

  // Initial fetch (and refetch whenever the recipient or the chip filter
  // changes). Inlined as a promise chain (not a `refetch()` call) so the
  // setState lands in a callback, not synchronously in the effect body.
  useEffect(() => {
    if (!recipientId) return
    let cancelled = false
    Promise.all([
      listNotifications({ limit: 50, types: typeFilter ?? undefined }),
      getUnreadCount(),
    ])
      .then(([rows, count]) => {
        if (cancelled) return
        setItems(rows)
        setUnreadCount(count)
      })
      .catch(() => {
        // Leave the last-known state in place; the next open retries.
      })
    return () => {
      cancelled = true
    }
  }, [recipientId, typeFilter])

  // Realtime subscription, scoped to this recipient. Exactly one instance of
  // this hook is alive per page (a layout owns it, either directly or through
  // <NotificationsProvider>), so a plain per-recipient topic is safe: two live
  // instances sharing one topic on a single socket would make either one's
  // unmount tear down the other's subscription.
  useEffect(() => {
    if (!recipientId) return
    const channel = supabase
      .channel(`notifications:${recipientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'core',
          table: 'notifications',
          filter: `recipient_id=eq.${recipientId}`,
        },
        () => {
          void refetch()
        },
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [recipientId, refetch])

  const onMarkRead = useCallback(
    async (id: string) => {
      // Optimistic: clear the row's unread state immediately.
      setItems((rows) =>
        rows.map((row) =>
          row.id === id && row.read_at === null
            ? { ...row, read_at: new Date().toISOString() }
            : row,
        ),
      )
      setUnreadCount((count) => Math.max(0, count - 1))
      try {
        await markRead(id)
      } finally {
        void refetch()
      }
    },
    [refetch],
  )

  const onMarkAllRead = useCallback(async () => {
    setItems((rows) =>
      rows.map((row) =>
        row.read_at === null ? { ...row, read_at: new Date().toISOString() } : row,
      ),
    )
    setUnreadCount(0)
    try {
      await markAllRead()
    } finally {
      void refetch()
    }
  }, [refetch])

  const onRemove = useCallback(
    async (id: string) => {
      // Optimistic: drop the row immediately; the badge re-syncs from the API
      // afterwards (`refetch` also refreshes the unread count).
      setItems((rows) => rows.filter((row) => row.id !== id))
      try {
        await deleteNotification(id)
      } catch (caught) {
        toastFailure(caught, 'Melding verwijderen mislukt.')
      } finally {
        void refetch()
      }
    },
    [refetch],
  )

  /**
   * "Gelezen wissen" (spec-64-u2, U-098): the read notifications in the active filter go,
   * with an 8-second undo; unread ones always stay. Until the toast is gone nothing is
   * deleted, so undo is a refetch of the unchanged server state.
   */
  const onClearRead = useCallback(() => {
    const read = items.filter((row) => row.read_at !== null)
    if (read.length === 0) return
    const ids = new Set(read.map((row) => row.id))
    toastDeferredDelete({
      message: 'Gelezen meldingen gewist.',
      hide: () => setItems((rows) => rows.filter((row) => !ids.has(row.id))),
      restore: () => void refetch(),
      commit: async () => {
        await clearAllNotifications({ readOnly: true, types: typeFilter })
        void refetch()
      },
      error: 'Meldingen wissen mislukt.',
    })
  }, [items, typeFilter, refetch])

  return {
    items,
    unreadCount,
    typeFilter,
    setTypeFilter,
    markRead: onMarkRead,
    markAllRead: onMarkAllRead,
    remove: onRemove,
    clearRead: onClearRead,
    refetch,
  }
}
